import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Paper, 
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { hotelService, roomAvailabilityService } from '../../services';
import { addDays } from 'date-fns';

const HotelAvailabilityChecker = () => {
  // State for form inputs
  const [hotelId, setHotelId] = useState('');
  const [roomTypeId, setRoomTypeId] = useState('');
  const [checkInDate, setCheckInDate] = useState(new Date());
  const [checkOutDate, setCheckOutDate] = useState(addDays(new Date(), 1));
  const [roomsNeeded, setRoomsNeeded] = useState(1);
  
  // State for data
  const [hotels, setHotels] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [availabilityResult, setAvailabilityResult] = useState(null);
  
  // State for UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Fetch hotels on component mount
  useEffect(() => {
    const fetchHotels = async () => {
      try {
        const hotelsData = await hotelService.fetchAllHotels();
        setHotels(hotelsData);
      } catch (err) {
        setError('Failed to load hotels. Please try again later.');
        console.error('Error fetching hotels:', err);
      }
    };

    fetchHotels();
  }, []);

  // Fetch room types when hotel is selected
  useEffect(() => {
    const fetchRoomTypes = async () => {
      if (!hotelId) {
        setRoomTypes([]);
        setRoomTypeId('');
        return;
      }

      try {
        const hotelData = await hotelService.fetchHotelById(hotelId);
        if (hotelData && hotelData.roomTypes) {
          setRoomTypes(hotelData.roomTypes);
        }
      } catch (err) {
        setError('Failed to load room types. Please try again later.');
        console.error('Error fetching room types:', err);
      }
    };

    fetchRoomTypes();
  }, [hotelId]);

  // Handle form submission
  const handleCheckAvailability = async (e) => {
    e.preventDefault();
    
    // Reset states
    setError(null);
    setSuccess(false);
    setAvailabilityResult(null);
    
    // Validate inputs
    if (!roomTypeId || !checkInDate || !checkOutDate) {
      setError('Please fill in all required fields.');
      return;
    }
    
    // Validate dates
    if (checkOutDate <= checkInDate) {
      setError('Check-out date must be after check-in date.');
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await roomAvailabilityService.checkRoomAvailability({
        roomTypeId,
        checkInDate,
        checkOutDate,
        roomsNeeded
      });
      
      setAvailabilityResult(result);
      setSuccess(true);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to check availability. Please try again.');
      setLoading(false);
      console.error('Error checking availability:', err);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper elevation={3} sx={{ p: 3, maxWidth: 800, mx: 'auto', my: 4 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          Check Hotel Availability
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Availability check completed successfully!
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleCheckAvailability} noValidate>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel id="hotel-select-label">Hotel</InputLabel>
                <Select
                  labelId="hotel-select-label"
                  id="hotel-select"
                  value={hotelId}
                  label="Hotel"
                  onChange={(e) => setHotelId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select a hotel</em>
                  </MenuItem>
                  {hotels.map((hotel) => (
                    <MenuItem key={hotel.id} value={hotel.id}>
                      {hotel.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required disabled={!hotelId}>
                <InputLabel id="room-type-select-label">Room Type</InputLabel>
                <Select
                  labelId="room-type-select-label"
                  id="room-type-select"
                  value={roomTypeId}
                  label="Room Type"
                  onChange={(e) => setRoomTypeId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select a room type</em>
                  </MenuItem>
                  {roomTypes.map((roomType) => (
                    <MenuItem key={roomType.id} value={roomType.id}>
                      {roomType.name} - ₹{roomType.basePrice}/night
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Check-in Date"
                value={checkInDate}
                onChange={(newValue) => setCheckInDate(newValue)}
                renderInput={(params) => <TextField {...params} fullWidth required />}
                disablePast
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Check-out Date"
                value={checkOutDate}
                onChange={(newValue) => setCheckOutDate(newValue)}
                renderInput={(params) => <TextField {...params} fullWidth required />}
                disablePast
                minDate={addDays(checkInDate, 1)}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="rooms-needed"
                label="Number of Rooms"
                type="number"
                InputProps={{ inputProps: { min: 1 } }}
                value={roomsNeeded}
                onChange={(e) => setRoomsNeeded(parseInt(e.target.value, 10) || 1)}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={loading || !roomTypeId}
                sx={{ mt: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Check Availability'}
              </Button>
            </Grid>
          </Grid>
        </Box>
        
        {availabilityResult && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Availability Results
            </Typography>
            
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="body1" color={availabilityResult.available ? 'success.main' : 'error.main'} gutterBottom>
                <strong>
                  {availabilityResult.available 
                    ? 'Rooms are available for your selected dates!' 
                    : 'Sorry, rooms are not available for the selected dates.'}
                </strong>
              </Typography>
              
              {availabilityResult.available && (
                <>
                  <Typography variant="body1" gutterBottom>
                    Total Price: ₹{availabilityResult.totalPrice.toFixed(2)} for {roomsNeeded} room(s)
                  </Typography>
                  
                  <Box sx={{ mt: 2 }}>
                    <Button 
                      variant="contained" 
                      color="success"
                      onClick={() => {
                        // Here you would implement booking functionality
                        alert('Booking functionality would be implemented here');
                      }}
                    >
                      Book Now
                    </Button>
                  </Box>
                </>
              )}
              
              {!availabilityResult.available && availabilityResult.unavailableDates.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    The following dates are unavailable:
                  </Typography>
                  <ul>
                    {availabilityResult.unavailableDates.map((date, index) => (
                      <li key={index}>
                        {new Date(date).toLocaleDateString()}
                      </li>
                    ))}
                  </ul>
                </Box>
              )}
            </Paper>
          </Box>
        )}
      </Paper>
    </LocalizationProvider>
  );
};

export default HotelAvailabilityChecker;
